import { Address, VerifyCardActionScreen } from '@varo/graphql-schema-types';
import { getExternalAddressText } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import React, { createContext, ReactNode, useContext, useState } from 'react';

type AddressContextType = {
  userCardAddresses: Address[];
  setUserCardAddresses: (addressList: Address[]) => void;
  addUserCardAddress: (address: Address) => void;
  userCardDetailsAddress: Address | undefined;
  setUserCardDetailsAddress: (address: Address) => void;
  shouldRefreshCards: boolean;
  setShouldRefreshCards: (shouldRefresh: boolean) => void;
  duplicateAddedAddress: Address | undefined;
  setDuplicateAddedAddress: (duplicateAddedAddress: Address) => void;
  verificationScreen?: VerifyCardActionScreen;
  setVerificationScreen: (verificationScreen: VerifyCardActionScreen) => void;
  updatedMessage: string;
  setUpdatedMessage: (message: string) => void;
  shouldRefreshTransactions: boolean;
  setShouldRefreshTransactions: (shouldRefresh: boolean) => void;
};

export const TabaPayAddressContext = createContext<
  AddressContextType | undefined
>(undefined);

export const useTabaPayAddress = () => {
  const context = useContext(TabaPayAddressContext);
  if (!context) {
    throw new Error(
      'useTabaPayAddress must be used within a TabaPayAddressProvider',
    );
  }
  return context;
};

export const TabaPayAddressProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [userCardAddresses, setUserCardAddresses] = useState<Address[]>([]);
  const [duplicateAddedAddress, setDuplicateAddedAddress] = useState<Address>();

  const addUserCardAddress = (address: Address) => {
    setUserCardAddresses((prev) => {
      const addressText = getExternalAddressText(address);
      const exists = prev.some(
        (a) => getExternalAddressText(a) === addressText,
      );
      if (exists) {
        setDuplicateAddedAddress(address);
      }
      return exists ? prev : [...prev, address];
    });
  };
  const [userCardDetailsAddress, setUserCardDetailsAddress] =
    useState<Address>();
  const [shouldRefreshCards, setShouldRefreshCards] = useState<boolean>(false);
  const [verificationScreen, setVerificationScreen] =
    useState<VerifyCardActionScreen>();
  const [updatedMessage, setUpdatedMessage] = useState<string>('');
  const [shouldRefreshTransactions, setShouldRefreshTransactions] =
    useState<boolean>(false);

  return (
    <TabaPayAddressContext.Provider
      value={{
        userCardAddresses,
        setUserCardAddresses,
        addUserCardAddress,
        userCardDetailsAddress,
        setUserCardDetailsAddress,
        shouldRefreshCards,
        setShouldRefreshCards,
        duplicateAddedAddress,
        setDuplicateAddedAddress,
        verificationScreen,
        setVerificationScreen,
        updatedMessage,
        setUpdatedMessage,
        shouldRefreshTransactions,
        setShouldRefreshTransactions,
      }}
    >
      {children}
    </TabaPayAddressContext.Provider>
  );
};
